import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, AlertCircle, ZoomIn, ZoomOut, Maximize, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { articles, sources, tierLabels, sentimentLabels } from '../lib/data';
export function ArticleDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  // In a real app, we'd fetch the article by ID. For now, we'll just use the first one if not found.
  const article = articles.find(a => a.id === id) || articles[0];
  const ConfidenceDot = ({
    level
  }: {
    level: 'high' | 'medium' | 'low';
  }) => {
    const colors = {
      high: 'bg-sentiment-positive',
      medium: 'bg-yellow-500',
      low: 'bg-sentiment-negative'
    };
    return <div className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} title={`Confidence: ${level}`} data-id="element-186" />;
  };
  return <div className="h-full flex flex-col bg-background" data-id="element-187">
      {/* Header */}
      <div className="h-14 border-b border-border bg-card flex items-center justify-between px-2 md:px-4 flex-shrink-0" data-id="element-188">
        <div className="flex items-center gap-2 md:gap-4 min-w-0" data-id="element-189">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate('/dashboard')} className="flex-shrink-0" data-id="element-190">
            <ChevronLeft className="h-4 w-4" data-id="element-191" />
          </Button>
          <div className="flex items-center gap-2 min-w-0" data-id="element-192">
            <Badge variant="outline" className="text-[10px] md:text-xs hidden sm:inline-flex flex-shrink-0" data-id="element-193">
              {article.source}
            </Badge>
            <span className="text-xs md:text-sm font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]" data-id="element-194">
              {article.title}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-195">
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-[10px] md:text-xs" data-id="element-196">
            Da validare
          </Badge>
        </div>
      </div>

      {/* Desktop Split View */}
      <div className="hidden md:flex flex-1 overflow-hidden" data-id="element-197">
        {/* Left: PDF Preview (60%) */}
        <div className="w-[60%] border-r border-border bg-muted/30 flex flex-col" data-id="element-198">
          <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-card" data-id="element-199">
            <span className="text-xs font-medium text-muted-foreground" data-id="element-200">
              Pagina {article.page} di 24
            </span>
            <div className="flex items-center gap-1" data-id="element-201">
              <Button variant="ghost" size="icon-sm" data-id="element-202">
                <ZoomOut className="h-4 w-4" data-id="element-203" />
              </Button>
              <span className="text-xs font-medium w-12 text-center" data-id="element-204">100%</span>
              <Button variant="ghost" size="icon-sm" data-id="element-205">
                <ZoomIn className="h-4 w-4" data-id="element-206" />
              </Button>
              <Separator orientation="vertical" className="h-4 mx-1" data-id="element-207" />
              <Button variant="ghost" size="icon-sm" data-id="element-208">
                <Maximize className="h-4 w-4" data-id="element-209" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-8 flex justify-center" data-id="element-210">
            {/* Simulated PDF Page */}
            <div className="w-[800px] bg-white shadow-sm border border-border relative" data-id="element-211">
              {/* Newspaper image */}
              <img src="https://cdn.magicpatterns.com/uploads/4FPWNcM3ZiRtHEZFBzjbKc/Screenshot_2026-04-22_alle_14.51.37.png" alt="Articolo di giornale" className="w-full h-auto" data-id="element-212" />
              {/* Simulated Highlighted Clipping */}
              <div className="absolute top-[12%] left-[5%] w-[90%] h-[35%] bg-accent/10 border-2 border-accent/50 rounded-sm" data-id="element-213">
                <div className="absolute -top-6 left-0 bg-accent text-white text-xs px-2 py-1 rounded-sm font-medium flex items-center gap-1" data-id="element-214">
                  <Sparkles className="h-3 w-3" data-id="element-215" />
                  Clipping Rilevato
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Validation Form (40%) */}
        <div className="w-[40%] flex flex-col bg-card" data-id="element-216">
          <div className="flex-1 overflow-auto p-6 space-y-8" data-id="element-217">
            {/* AI Notice */}

            {/* Form Fields */}
            <div className="space-y-6" data-id="element-218">
              {/* Source & Date */}
              <div className="grid grid-cols-2 gap-4" data-id="element-219">
                <div className="space-y-2" data-id="element-220">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-221">
                    Testata <ConfidenceDot level="high" data-id="element-222" />
                  </label>
                  <Select defaultValue={article.source} data-id="element-223">
                    <SelectTrigger data-id="element-224">
                      <SelectValue data-id="element-225" />
                    </SelectTrigger>
                    <SelectContent data-id="element-226">
                      {sources.map(s => <SelectItem key={s.name} value={s.name} data-id="element-227">
                          {s.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2" data-id="element-228">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-229">
                    Data <ConfidenceDot level="high" data-id="element-230" />
                  </label>
                  <input type="date" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.date} data-id="element-231" />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2" data-id="element-232">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-233">
                  Titolo Articolo <ConfidenceDot level="medium" data-id="element-234" />
                </label>
                <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.title} data-id="element-235" />
              </div>

              {/* Author */}
              <div className="space-y-2" data-id="element-236">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-237">
                  Firma / Autore <ConfidenceDot level="high" data-id="element-238" />
                </label>
                <div className="flex items-center gap-4" data-id="element-239">
                  <input type="text" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.author} data-id="element-240" />
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-241">
                    <Switch id="authoritative" defaultChecked={article.isAuthoritative} data-id="element-242" />
                    <label htmlFor="authoritative" className="text-sm font-medium" data-id="element-243">
                      Autorevole
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-244" />

              {/* Content Type */}
              <div className="space-y-3" data-id="element-245">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-246">
                  Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-247" />
                </label>
                <RadioGroup defaultValue={article.contentType} className="grid grid-cols-2 gap-2" data-id="element-248">
                  <div className="flex items-center space-x-2" data-id="element-249">
                    <RadioGroupItem value="dedicated_article" id="r1" data-id="element-250" />
                    <label htmlFor="r1" className="text-sm" data-id="element-251">
                      Articolo dedicato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-252">
                    <RadioGroupItem value="interview" id="r2" data-id="element-253" />
                    <label htmlFor="r2" className="text-sm" data-id="element-254">
                      Intervista
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-255">
                    <RadioGroupItem value="signed_commentary" id="r3" data-id="element-256" />
                    <label htmlFor="r3" className="text-sm" data-id="element-257">
                      Commento firmato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-258">
                    <RadioGroupItem value="mention" id="r4" data-id="element-259" />
                    <label htmlFor="r4" className="text-sm" data-id="element-260">
                      Citazione
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <Separator data-id="element-261" />

              {/* Positioning */}
              <div className="space-y-3" data-id="element-262">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-263">
                  Posizionamento <ConfidenceDot level="low" data-id="element-264" />
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-265">
                  <div className="flex items-center space-x-2" data-id="element-266">
                    <Checkbox id="c1" defaultChecked={article.hasPhoto} data-id="element-267" />
                    <label htmlFor="c1" className="text-sm" data-id="element-268">
                      Foto presente
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-269">
                    <Checkbox id="c2" defaultChecked={article.titleMentionsClient} data-id="element-270" />
                    <label htmlFor="c2" className="text-sm" data-id="element-271">
                      Cliente nel titolo
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-272">
                    <Checkbox id="c3" defaultChecked={article.boldedReference} data-id="element-273" />
                    <label htmlFor="c3" className="text-sm" data-id="element-274">
                      Citazione in grassetto
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-275">
                    <Checkbox id="c4" defaultChecked={article.frontPageCallout} data-id="element-276" />
                    <label htmlFor="c4" className="text-sm" data-id="element-277">
                      Richiamo in prima
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-278" />

              {/* Sentiment */}
              <div className="space-y-3" data-id="element-279">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-280">
                  Sentiment <ConfidenceDot level="high" data-id="element-281" />
                </label>
                <div className="flex gap-2" data-id="element-282">
                  <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} className={article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''} data-id="element-283">
                    Positivo
                  </Button>
                  <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} className={article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''} data-id="element-284">
                    Neutro
                  </Button>
                  <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} className={article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''} data-id="element-285">
                    Negativo
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Score Card */}
          <div className="p-6 border-t border-border bg-muted/10" data-id="element-286">
            <div className="flex items-end justify-between mb-4" data-id="element-287">
              <div data-id="element-288">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-289">
                  Score Rilevanza
                </div>
                <div className="text-5xl font-bold text-accent" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-290">
                  {article.relevanceScore.toFixed(1)}
                </div>
              </div>
              <div className="text-right" data-id="element-291">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-292">
                  Valore AVE
                </div>
                <div className="text-2xl font-semibold" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-293">
                  €{article.ave.toLocaleString('it-IT')}
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="bg-card border border-border rounded-md p-3 mb-6" data-id="element-294">
              <div className="text-xs font-medium mb-2" data-id="element-295">Dettaglio Score:</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-id="element-296">
                <div className="flex justify-between" data-id="element-297">
                  <span className="text-muted-foreground" data-id="element-298">Tier testata:</span>
                  <span className="font-medium" data-id="element-299">
                    +{article.scoreBreakdown.tier}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-300">
                  <span className="text-muted-foreground" data-id="element-301">Posizionamento:</span>
                  <span className="font-medium" data-id="element-302">
                    +{article.scoreBreakdown.positioning}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-303">
                  <span className="text-muted-foreground" data-id="element-304">Tipologia:</span>
                  <span className="font-medium" data-id="element-305">
                    +{article.scoreBreakdown.contentType}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-306">
                  <span className="text-muted-foreground" data-id="element-307">Sentiment:</span>
                  <span className="font-medium" data-id="element-308">
                    +{article.scoreBreakdown.sentiment}
                  </span>
                </div>
              </div>
            </div>

            <Button className="w-full h-14 text-base font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-309">
              Approva e Prosegui
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Tabbed View */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden" data-id="element-310">
        <Tabs defaultValue="validation" className="flex-1 flex flex-col h-full" data-id="element-311">
          <div className="px-4 pt-4 pb-2 bg-card border-b border-border flex-shrink-0" data-id="element-312">
            <TabsList className="w-full grid grid-cols-2" data-id="element-313">
              <TabsTrigger value="pdf" className="text-xs" data-id="element-314">
                Anteprima PDF
              </TabsTrigger>
              <TabsTrigger value="validation" className="text-xs" data-id="element-315">
                Validazione
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="pdf" className="flex-1 m-0 overflow-auto bg-muted/30 p-4 flex justify-center" data-id="element-316">
            {/* Mobile Simulated PDF Page */}
            <div className="w-full max-w-[400px] aspect-[1/1.4] bg-white shadow-sm border border-border relative transform scale-100 origin-top" data-id="element-317">
              <div className="absolute top-[15%] left-[10%] w-[80%] h-[40%] bg-accent/10 border-2 border-accent/50 rounded-sm" data-id="element-318">
                <div className="absolute -top-5 left-0 bg-accent text-white text-[10px] px-1.5 py-0.5 rounded-sm font-medium flex items-center gap-1" data-id="element-319">
                  <Sparkles className="h-2 w-2" data-id="element-320" />
                  Clipping
                </div>
              </div>
              <div className="p-6 space-y-3 opacity-20" data-id="element-321">
                <div className="h-6 bg-gray-300 w-3/4 mb-4" data-id="element-322" />
                <div className="h-2 bg-gray-300 w-full" data-id="element-323" />
                <div className="h-2 bg-gray-300 w-full" data-id="element-324" />
                <div className="h-2 bg-gray-300 w-5/6" data-id="element-325" />
                <div className="h-2 bg-gray-300 w-full mt-4" data-id="element-326" />
                <div className="h-2 bg-gray-300 w-4/5" data-id="element-327" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="validation" className="flex-1 m-0 overflow-auto flex flex-col bg-card" data-id="element-328">
            <div className="flex-1 p-4 space-y-6" data-id="element-329">
              {/* AI Notice */}
              <div className="bg-blue-50 border border-blue-100 rounded-md p-3 flex items-start gap-2" data-id="element-330">
                <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-331" />
                <div data-id="element-332">
                  <h4 className="text-xs font-medium text-blue-900" data-id="element-333">
                    Estrazione AI completata
                  </h4>
                  <p className="text-[10px] text-blue-700 mt-1" data-id="element-334">
                    I campi con il pallino verde hanno un'alta confidenza.
                  </p>
                </div>
              </div>

              {/* Form Fields - Mobile Layout */}
              <div className="space-y-5" data-id="element-335">
                <div className="grid grid-cols-2 gap-3" data-id="element-336">
                  <div className="space-y-1.5" data-id="element-337">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-338">
                      Testata <ConfidenceDot level="high" data-id="element-339" />
                    </label>
                    <Select defaultValue={article.source} data-id="element-340">
                      <SelectTrigger className="h-9 text-xs" data-id="element-341">
                        <SelectValue data-id="element-342" />
                      </SelectTrigger>
                      <SelectContent data-id="element-343">
                        {sources.map(s => <SelectItem key={s.name} value={s.name} className="text-xs" data-id="element-344">
                            {s.name}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5" data-id="element-345">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-346">
                      Data <ConfidenceDot level="high" data-id="element-347" />
                    </label>
                    <input type="date" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.date} data-id="element-348" />
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-349">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-350">
                    Titolo Articolo <ConfidenceDot level="medium" data-id="element-351" />
                  </label>
                  <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-2 py-2 text-xs ring-offset-background" defaultValue={article.title} data-id="element-352" />
                </div>

                <div className="space-y-1.5" data-id="element-353">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-354">
                    Firma / Autore <ConfidenceDot level="high" data-id="element-355" />
                  </label>
                  <div className="flex flex-col gap-2" data-id="element-356">
                    <input type="text" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.author} data-id="element-357" />
                    <div className="flex items-center gap-2" data-id="element-358">
                      <Switch id="authoritative-mobile" defaultChecked={article.isAuthoritative} className="scale-75 origin-left" data-id="element-359" />
                      <label htmlFor="authoritative-mobile" className="text-xs font-medium" data-id="element-360">
                        Autorevole
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-361" />

                <div className="space-y-2" data-id="element-362">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-363">
                    Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-364" />
                  </label>
                  <RadioGroup defaultValue={article.contentType} className="grid grid-cols-1 gap-2" data-id="element-365">
                    <div className="flex items-center space-x-2" data-id="element-366">
                      <RadioGroupItem value="dedicated_article" id="rm1" className="w-3 h-3" data-id="element-367" />
                      <label htmlFor="rm1" className="text-xs" data-id="element-368">
                        Articolo dedicato
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-369">
                      <RadioGroupItem value="interview" id="rm2" className="w-3 h-3" data-id="element-370" />
                      <label htmlFor="rm2" className="text-xs" data-id="element-371">
                        Intervista
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-372">
                      <RadioGroupItem value="signed_commentary" id="rm3" className="w-3 h-3" data-id="element-373" />
                      <label htmlFor="rm3" className="text-xs" data-id="element-374">
                        Commento firmato
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                <Separator data-id="element-375" />

                <div className="space-y-2" data-id="element-376">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-377">
                    Posizionamento <ConfidenceDot level="low" data-id="element-378" />
                  </label>
                  <div className="grid grid-cols-1 gap-2" data-id="element-379">
                    <div className="flex items-center space-x-2" data-id="element-380">
                      <Checkbox id="cm1" defaultChecked={article.hasPhoto} className="w-3 h-3" data-id="element-381" />
                      <label htmlFor="cm1" className="text-xs" data-id="element-382">
                        Foto presente
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-383">
                      <Checkbox id="cm2" defaultChecked={article.titleMentionsClient} className="w-3 h-3" data-id="element-384" />
                      <label htmlFor="cm2" className="text-xs" data-id="element-385">
                        Cliente nel titolo
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-386">
                      <Checkbox id="cm3" defaultChecked={article.boldedReference} className="w-3 h-3" data-id="element-387" />
                      <label htmlFor="cm3" className="text-xs" data-id="element-388">
                        Citazione in grassetto
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-389" />

                <div className="space-y-2" data-id="element-390">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-391">
                    Sentiment <ConfidenceDot level="high" data-id="element-392" />
                  </label>
                  <div className="flex gap-1.5" data-id="element-393">
                    <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''}`} data-id="element-394">
                      Positivo
                    </Button>
                    <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''}`} data-id="element-395">
                      Neutro
                    </Button>
                    <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''}`} data-id="element-396">
                      Negativo
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Bottom Score Card */}
            <div className="p-4 border-t border-border bg-muted/10 flex-shrink-0" data-id="element-397">
              <div className="flex items-end justify-between mb-3" data-id="element-398">
                <div data-id="element-399">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-400">
                    Score Rilevanza
                  </div>
                  <div className="text-3xl font-bold text-accent" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-401">
                    {article.relevanceScore.toFixed(1)}
                  </div>
                </div>
                <div className="text-right" data-id="element-402">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-403">
                    Valore AVE
                  </div>
                  <div className="text-lg font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-404">
                    €{article.ave.toLocaleString('it-IT')}
                  </div>
                </div>
              </div>
              <Button className="w-full h-12 text-sm font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-405">
                Approva e Prosegui
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>;
}